import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    Switch,
    FormControlLabel,
    InputAdornment,
    ListSubheader,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { SCUIGeneralDrawer, SCUIGeneralDrawerHeader } from '../../../atomic/molecules';
import { useTranslation } from 'react-i18next';
import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import Swal from 'sweetalert2';
import CutOffCommand from '../../../services/command/cutOff/CutOffCommand.services';
import CutOffQuery from '../../../services/query/cutOff/CutOffQuery.services';
import { COED } from '../cutOff.classes';
import { loadCutOffFilterOptions } from '../cutOffFilterOptions.helpers';
import {
    buildCutOffSaveItem,
    getEmptyCutOffFormState,
    resolveCutOffSaveErrorMessage,
} from '../cutOffAddEditDrawer.helpers';

interface AddEditDrawerProps {
    isDrawerOpen: boolean;
    handleDrawerClose: () => void;
    editData: any | null;
    cargoCompanyOptions?: { id: number; name: string }[];
    onSave?: (data: any) => void;
    setIsLoading?: (loading: boolean) => void;
}

const AddEditDrawer = ({
    isDrawerOpen,
    handleDrawerClose,
    editData,
    cargoCompanyOptions: cargoCompanyOptionsProp = [],
    onSave,
    setIsLoading,
}: AddEditDrawerProps) => {
    const { t } = useTranslation();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [cargoCompanyOptions, setCargoCompanyOptions] =
        useState<{ id: number; name: string }[]>(cargoCompanyOptionsProp);
    const [distanceOptions, setDistanceOptions] = useState<{ id: number; name: string }[]>([]);
    const [deliveryTypeOptions, setDeliveryTypeOptions] = useState<any[]>([]);
    const [departureLocationOptions, setDepartureLocationOptions] = useState<{ id: number; name: string }[]>([]);
    const [arrivalLocationOptions, setArrivalLocationOptions] = useState<{ id: number; name: string }[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState<boolean>(false);
    const [addressTypeId, setAddressTypeId] = useState<number | null>(null);

    // Update cargoCompanyOptions when prop changes
    useEffect(() => {
        if (cargoCompanyOptionsProp?.length > 0) {
            setCargoCompanyOptions(cargoCompanyOptionsProp);
        }
    }, [cargoCompanyOptionsProp]);

    // Search states for each select
    const [searchCargoCompany, setSearchCargoCompany] = useState('');
    const [searchDistance, setSearchDistance] = useState('');
    const [searchDeliveryType, setSearchDeliveryType] = useState('');
    const [searchDepartureLocation, setSearchDepartureLocation] = useState('');
    const [searchArrivalLocation, setSearchArrivalLocation] = useState('');

    const isEditMode = editData != null;

    const getInitialFormState = () => ({
        shippingCompanyId: editData?.shippingCompanyId ?? '',
        cutoffTime: editData?.cutoffTime ?? '',
        commitmentDay: editData?.commitmentDay ?? '',
        validFromDate: editData?.validFromDate ? editData.validFromDate.split('T')[0] : '',
        validToDate: editData?.validToDate ? editData.validToDate.split('T')[0] : '',
        isActive: editData?.isActive ?? true,
        departureLocationId: editData?.departureLocationId ?? '',
        arrivalLocationId: editData?.arrivalLocationId ?? '',
        distanceRangeId: editData?.distanceRangeId ?? '',
        deliveryTypeId: editData?.deliveryTypeId ?? '',
    });

    const [formState, setFormState] = useState<any>(getInitialFormState());

    // Reset form when drawer opens or editData changes
    useEffect(() => {
        if (isDrawerOpen) {
            setFormState(getInitialFormState());
        }
    }, [isDrawerOpen, editData]);

    // Fetch filter options when drawer opens
    useEffect(() => {
        const needsCargoCompanies = cargoCompanyOptionsProp?.length === 0 && cargoCompanyOptions?.length === 0;
        const needsDistanceRanges = distanceOptions?.length === 0;
        const needsDeliveryTypes = deliveryTypeOptions?.length === 0;
        const needsDepartureOptions = departureLocationOptions?.length === 0;
        const needsArrivalOptions = arrivalLocationOptions?.length === 0;
        const needsLocationOptions = needsDepartureOptions || needsArrivalOptions;

        if (
            !isDrawerOpen ||
            (!needsCargoCompanies &&
                !needsDistanceRanges &&
                !needsDeliveryTypes &&
                !needsLocationOptions) ||
            isLoadingOptions
        ) {
            return;
        }

        setIsLoadingOptions(true);
        loadCutOffFilterOptions(
            {
                needsCargoCompanies,
                needsDistanceRanges,
                needsDeliveryTypes,
                needsDepartureOptions,
                needsArrivalOptions,
            },
            {
                setCargoCompanyOptions,
                setDistanceOptions,
                setDeliveryTypeOptions,
                setDepartureLocationOptions,
                setArrivalLocationOptions,
                setAddressTypeId,
            }
        )
            .catch((err: any) => {
                console.error('Error fetching filter options:', err);
            })
            .finally(() => {
                setIsLoadingOptions(false);
            });
    }, [isDrawerOpen]);

    // Auto-set distance when departure and arrival locations are selected (add mode only)
    useEffect(() => {
        if (isEditMode) {
            return;
        }

        let isCancelled = false;

        const departureLocationId = formState?.departureLocationId;
        const arrivalLocationId = formState?.arrivalLocationId;

        if (!departureLocationId || !arrivalLocationId) {
            return;
        }

        CutOffQuery.GetLocationDistanceMatrix({
            departureLocationId: Number(departureLocationId),
            arrivalLocationId: Number(arrivalLocationId),
        })
            .then((res: any) => {
                if (isCancelled) return;

                if (res?.isSuccess && res?.distanceRangeId != null) {
                    setFormState((prev: any) => ({
                        ...prev,
                        distanceRangeId: res.distanceRangeId,
                    }));
                }
            })
            .catch((err: any) => {
                if (!isCancelled) {
                    console.error('Error fetching location distance matrix:', err);
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [formState?.departureLocationId, formState?.arrivalLocationId, isEditMode]);

    const handleInputChange = (field: string) => (event: any) => {
        const value = event?.target?.value ?? '';
        setFormState((prev: any) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleSwitchChange = (event: any) => {
        setFormState((prev: any) => ({
            ...prev,
            isActive: event?.target?.checked ?? false,
        }));
    };

    const handleCommitmentDayChange = (event: any) => {
        // Only allow positive integers, no dots or commas
        const value = event?.target?.value ?? '';
        const sanitizedValue = value.replace(/\D/g, '');
        setFormState((prev: any) => ({
            ...prev,
            commitmentDay: sanitizedValue,
        }));
    };

    const handleCommitmentDayKeyDown = (event: any) => {
        // Prevent dots, commas, and minus signs
        if (event.key === '.' || event.key === ',' || event.key === '-' || event.key === 'e' || event.key === 'E') {
            event.preventDefault();
        }
    };

    const handleClear = () => {
        if (isEditMode) {
            // Reset to original edit data
            setFormState(getInitialFormState());
        } else {
            // Clear all fields for create mode
            setFormState(getEmptyCutOffFormState());
        }
    };

    // Generic filter helper with locale support (especially for Turkish İ/i case conversion)
    const filterOptions = <T,>(options: T[], search: string, getLabel: (opt: T) => string | undefined): T[] => {
        if (!search?.trim()) return options;
        const locale = localStorage.getItem('lan') || 'tr-TR';
        const searchLower = search.toLocaleLowerCase(locale);
        return options.filter((opt) => getLabel(opt)?.toLocaleLowerCase(locale)?.includes(searchLower));
    };

    const filteredCargoCompanyOptions = useMemo(
        () => filterOptions(cargoCompanyOptions ?? [], searchCargoCompany, (opt) => opt?.name),
        [cargoCompanyOptions, searchCargoCompany]
    );
    const filteredDistanceOptions = useMemo(
        () => filterOptions(distanceOptions ?? [], searchDistance, (opt) => opt?.name),
        [distanceOptions, searchDistance]
    );
    const filteredDeliveryTypeOptions = useMemo(
        () => filterOptions(deliveryTypeOptions ?? [], searchDeliveryType, (opt: any) => opt?.deliveryTypeTranslated),
        [deliveryTypeOptions, searchDeliveryType]
    );
    const filteredDepartureLocationOptions = useMemo(
        () => filterOptions(departureLocationOptions ?? [], searchDepartureLocation, (opt) => opt?.name),
        [departureLocationOptions, searchDepartureLocation]
    );
    const filteredArrivalLocationOptions = useMemo(
        () => filterOptions(arrivalLocationOptions ?? [], searchArrivalLocation, (opt) => opt?.name),
        [arrivalLocationOptions, searchArrivalLocation]
    );

    // Search input component
    const renderSearchInput = (searchValue: string, setSearchValue: (val: string) => void) => (
        <ListSubheader sx={{ backgroundColor: 'white' }}>
            <TextField
                className={COED.inputMenuSearch}
                size="small"
                fullWidth
                autoFocus
                placeholder={t('general.search') ?? 'Search...'}
                value={searchValue}
                onChange={(e) => setSearchValue(e?.target?.value ?? '')}
                onKeyDown={(e) => {
                    if (e.key !== 'Escape') {
                        e.stopPropagation();
                    }
                }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    },
                }}
            />
        </ListSubheader>
    );

    const handleSave = async () => {
        const selectedCompany = cargoCompanyOptions?.find((c) => c.id === Number(formState?.shippingCompanyId));

        if (!formState?.shippingCompanyId || !selectedCompany) {
            Swal.fire({
                icon: 'warning',
                title: t('general.warning'),
                text: t('cutOff.selectCargoCompanyWarning'),
                confirmButtonText: t('general.ok'),
            });
            return;
        }

        setIsSaving(true);
        setIsLoading?.(true);

        try {
            const userName = JSON.parse(localStorage.getItem('checkOperator') || '{}')?.username ?? '';

            // Find selected option names
            const selectedDepartureLocation = departureLocationOptions?.find(
                (loc) => loc.id === Number(formState?.departureLocationId)
            );
            const selectedArrivalLocation = arrivalLocationOptions?.find(
                (loc) => loc.id === Number(formState?.arrivalLocationId)
            );
            const selectedDistanceRange = distanceOptions?.find(
                (dist) => dist.id === Number(formState?.distanceRangeId)
            );
            const selectedDeliveryType = deliveryTypeOptions?.find(
                (dt) => dt.deliveryTypeId === Number(formState?.deliveryTypeId)
            );

            const cutOffItem = buildCutOffSaveItem({
                formState,
                addressTypeId,
                selectedDepartureLocation,
                selectedArrivalLocation,
                selectedDistanceRange,
                selectedDeliveryType,
            });

            const requestPayload = {
                shippingCompanyId: Number(formState?.shippingCompanyId),
                shippingCompanyName: selectedCompany?.name ?? '',
                updatedBy: userName,
                shippingCompanyCutOffList: [cutOffItem],
            };
            const response = await CutOffCommand.UpsertBulkShippingCompanyCutOff(requestPayload);

            if (response) {
                Swal.fire({
                    icon: 'success',
                    title: t('general.success'),
                    text: isEditMode ? t('cutOff.editSuccess') : t('cutOff.addSuccess'),
                    showConfirmButton: false,
                    timer: 2000,
                });
                onSave?.(response);
                handleDrawerClose();
            }
        } catch (error: any) {
            console.error('Save error:', error);
            const errorMessage = resolveCutOffSaveErrorMessage(error, t('cutOff.saveError'));

            Swal.fire({
                icon: 'error',
                title: t('general.error'),
                text: errorMessage,
                confirmButtonText: t('general.ok'),
            });
        } finally {
            setIsSaving(false);
            setIsLoading?.(false);
        }
    };

    return (
        <SCUIGeneralDrawer width="480px" open={isDrawerOpen} onClose={handleDrawerClose}>
            <SCUIGeneralDrawerHeader
                header={isEditMode ? t('cutOff.editCutOff') : t('cutOff.addNewCutOff')}
                onClose={handleDrawerClose}
            />
            <SCUILoadingLinear display={isSaving} />
            <Box className={COED.drawerBody} sx={{ padding: '16px', display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Form Container */}
                <Box className={COED.formFields} sx={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
                    {/* Cargo Company */}
                    <FormControl className={COED.fieldCargoCompany} fullWidth>
                        <InputLabel id="cargoCompany-label">{t('cutOff.cargoCompany')}</InputLabel>
                        <Select
                            className={COED.inputSelectCargoCompany}
                            labelId="cargoCompany-label"
                            value={formState?.shippingCompanyId ?? ''}
                            onChange={handleInputChange('shippingCompanyId')}
                            label={t('cutOff.cargoCompany')}
                            disabled={isEditMode || isLoadingOptions}
                            onClose={() => setSearchCargoCompany('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchCargoCompany, setSearchCargoCompany)}
                            {filteredCargoCompanyOptions.map((option) => (
                                <MenuItem key={option?.id} value={option?.id}>
                                    {option?.name ?? ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Departure Location */}
                    <FormControl className={COED.fieldDepartureLocation} fullWidth>
                        <InputLabel id="departureLocation-label">{t('cutOff.departureLocation')}</InputLabel>
                        <Select
                            className={COED.inputSelectDepartureLocation}
                            labelId="departureLocation-label"
                            value={formState?.departureLocationId ?? ''}
                            onChange={handleInputChange('departureLocationId')}
                            label={t('cutOff.departureLocation')}
                            disabled={isLoadingOptions}
                            onClose={() => setSearchDepartureLocation('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchDepartureLocation, setSearchDepartureLocation)}
                            {filteredDepartureLocationOptions.map((option) => (
                                <MenuItem key={option?.id} value={option?.id}>
                                    {option?.name ?? ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Arrival Location */}
                    <FormControl className={COED.fieldArrivalLocation} fullWidth>
                        <InputLabel id="arrivalLocation-label">{t('cutOff.arrivalLocation')}</InputLabel>
                        <Select
                            className={COED.inputSelectArrivalLocation}
                            labelId="arrivalLocation-label"
                            value={formState?.arrivalLocationId ?? ''}
                            onChange={handleInputChange('arrivalLocationId')}
                            label={t('cutOff.arrivalLocation')}
                            disabled={isLoadingOptions}
                            onClose={() => setSearchArrivalLocation('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchArrivalLocation, setSearchArrivalLocation)}
                            {filteredArrivalLocationOptions.map((option) => (
                                <MenuItem key={option?.id} value={option?.id}>
                                    {option?.name ?? ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Distance Range */}
                    <FormControl className={COED.fieldDistance} fullWidth>
                        <InputLabel id="distanceRange-label">{t('cutOff.distance')}</InputLabel>
                        <Select
                            className={COED.inputSelectDistance}
                            labelId="distanceRange-label"
                            value={formState?.distanceRangeId ?? ''}
                            onChange={handleInputChange('distanceRangeId')}
                            label={t('cutOff.distance')}
                            disabled={isLoadingOptions}
                            onClose={() => setSearchDistance('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchDistance, setSearchDistance)}
                            {filteredDistanceOptions.map((option) => (
                                <MenuItem key={option?.id} value={option?.id}>
                                    {option?.name ?? ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Delivery Type */}
                    <FormControl className={COED.fieldDeliveryType} fullWidth>
                        <InputLabel id="deliveryType-label">{t('cutOff.deliveryType')}</InputLabel>
                        <Select
                            className={COED.inputSelectDeliveryType}
                            labelId="deliveryType-label"
                            value={formState?.deliveryTypeId ?? ''}
                            onChange={handleInputChange('deliveryTypeId')}
                            label={t('cutOff.deliveryType')}
                            disabled={isLoadingOptions}
                            onClose={() => setSearchDeliveryType('')}
                            MenuProps={{ autoFocus: false }}
                        >
                            {renderSearchInput(searchDeliveryType, setSearchDeliveryType)}
                            {filteredDeliveryTypeOptions.map((option: any) => (
                                <MenuItem key={option?.deliveryTypeId} value={option?.deliveryTypeId}>
                                    {option?.deliveryTypeTranslated ?? ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Cut Off Time */}
                    <TextField
                        className={COED.inputTextfieldCutOffTime}
                        fullWidth
                        label={t('cutOff.cutOffTime')}
                        type="time"
                        value={formState?.cutoffTime ?? ''}
                        onChange={handleInputChange('cutoffTime')}
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                            htmlInput: {
                                step: 60, // 1 minute steps
                            },
                        }}
                    />

                    {/* Commitment Day */}
                    <TextField
                        className={COED.inputTextfieldCommitmentDay}
                        fullWidth
                        label={t('cutOff.commitmentDay')}
                        type="text"
                        value={formState?.commitmentDay ?? ''}
                        onChange={handleCommitmentDayChange}
                        onKeyDown={handleCommitmentDayKeyDown}
                        slotProps={{
                            htmlInput: {
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                            },
                        }}
                    />

                    {/* Valid From Date */}
                    <TextField
                        className={COED.inputTextfieldValidFrom}
                        fullWidth
                        label={t('cutOff.startDate')}
                        type="date"
                        value={formState?.validFromDate ?? ''}
                        onChange={handleInputChange('validFromDate')}
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                            htmlInput: {
                                type: 'date',
                            },
                        }}
                    />

                    {/* Valid To Date */}
                    <TextField
                        className={COED.inputTextfieldValidTo}
                        fullWidth
                        label={t('cutOff.endDate')}
                        type="date"
                        value={formState?.validToDate ?? ''}
                        onChange={handleInputChange('validToDate')}
                        slotProps={{
                            inputLabel: {
                                shrink: true,
                            },
                            htmlInput: {
                                type: 'date',
                            },
                        }}
                    />

                    {/* Is Active */}
                    <FormControlLabel
                        className={COED.rowActiveStatus}
                        control={
                            <Switch
                                className={COED.inputSwitchActiveStatus}
                                checked={formState?.isActive ?? false}
                                onChange={handleSwitchChange}
                                color="primary"
                            />
                        }
                        label={t('cutOff.activeStatus')}
                        sx={{ marginLeft: 0 }}
                    />
                </Box>

                {/* Buttons */}
                <Box
                    className={COED.actionsRow}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '8px',
                        width: '100%',
                        padding: '12px',
                        marginTop: 'auto',
                    }}
                >
                    <SCUIButton
                        className={COED.inputButtonClear}
                        text={t('general.clear')}
                        onClick={handleClear}
                        buttonType="button"
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        style={{ backgroundColor: 'red', borderColor: 'red' }}
                    />
                    <SCUIButton
                        className={COED.inputButtonSave}
                        text={t('general.save')}
                        type="primary"
                        sizeType="fixed"
                        size={'l'}
                        buttonType="button"
                        onClick={handleSave}
                        disabled={isSaving}
                    />
                </Box>
            </Box>
        </SCUIGeneralDrawer>
    );
};

export default AddEditDrawer;
